/**
 * URL utilities for handling redirect URIs and external navigation
 */

/**
 * Append TSP count to a URL as a query parameter
 * @param url - The base URL to append the count to
 * @param tspCount - The number of active TSP connections
 * @returns URL with tsp_count parameter added
 */
function appendTspCountToUrl(url: string, tspCount: number): string {
  if (!url || url === 'null') {
    return url;
  }

  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set('tsp_count', tspCount.toString());
    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails, try simple string concatenation for relative URLs
    console.warn(
      '[urlUtils] Failed to parse URL, using string concatenation:',
      error
    );
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}tsp_count=${tspCount}`;
  }
}

function sanitizeParams(
  params?: Record<string, string | undefined>
): Record<string, string> | undefined {
  if (!params) {
    return undefined;
  }

  const sanitizedEntries = Object.entries(params)
    .map(([key, value]) => {
      if (typeof value !== 'string') {
        return null;
      }
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      return [key, trimmed] as [string, string];
    })
    .filter(Boolean) as Array<[string, string]>;

  if (sanitizedEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(sanitizedEntries);
}

export function appendQueryParamsToUrl(
  url: string,
  params?: Record<string, string | undefined>
): string {
  const sanitized = sanitizeParams(params);
  if (!sanitized) {
    return url;
  }

  const [baseAndQuery, hash] = url.split('#');
  const [base, query] = baseAndQuery.split('?');
  const searchParams = new URLSearchParams(query || '');

  Object.entries(sanitized).forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  const newQuery = searchParams.toString();
  const rebuilt = newQuery ? `${base}?${newQuery}` : base;
  return hash ? `${rebuilt}#${hash}` : rebuilt;
}

/**
 * Get TSP count from localStorage or return 0
 * This reads the current TSP count without needing React context
 */
function getTspCountFromStorage(): number {
  try {
    // Try to get from app state in localStorage (if it exists)
    const appState = localStorage.getItem('appState');
    if (appState) {
      const parsed = JSON.parse(appState);
      return parsed.tspCount || 0;
    }

    // Fallback: try to get from a dedicated tsp_count key
    const tspCount = localStorage.getItem('tsp_count');
    return tspCount ? parseInt(tspCount, 10) : 0;
  } catch (error) {
    console.warn('[urlUtils] Failed to get TSP count from storage:', error);
    return 0;
  }
}

/**
 * Normalize a URL to ensure it has a proper protocol
 * This handles cases where users enter URLs like "www.google.com" without http/https
 */
export function normalizeRedirectUrl(
  url: string | null | undefined
): string | null {
  if (!url || url === 'null' || url.trim() === '') {
    return null;
  }

  const trimmedUrl = url.trim();

  // If it already has a protocol, return as-is
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }

  // Handle special internal routes (relative paths)
  if (trimmedUrl.startsWith('/')) {
    return trimmedUrl;
  }

  // Check if it looks like a domain name that should be an absolute URL
  if (
    trimmedUrl.includes('.') &&
    (trimmedUrl.startsWith('www.') ||
      trimmedUrl.match(
        /^[\w-]+\.(com|org|net|edu|gov|mil|int|co|io|ly|me|app|dev|local)($|\/)/i
      ) ||
      trimmedUrl.match(
        /^[\w-]+\.[\w-]+\.(com|org|net|edu|gov|mil|int|co|io|ly|me|app|dev|local)($|\/)/i
      ))
  ) {
    // Default to https for domain names
    return `https://${trimmedUrl}`;
  }

  // For localhost or IP addresses, default to http
  if (
    trimmedUrl.startsWith('localhost') ||
    trimmedUrl.match(/^127\.0\.0\.1/) ||
    trimmedUrl.match(/^192\.168\./) ||
    trimmedUrl.match(/^10\./) ||
    trimmedUrl.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/)
  ) {
    return `http://${trimmedUrl}`;
  }

  // If we can't determine the type, treat as relative path
  return trimmedUrl;
}

/**
 * Safely redirect to an external URL with proper protocol handling
 * Automatically appends TSP count to external URLs
 */
export function safeRedirect(
  url: string | null | undefined,
  fallback?: () => void,
  extraParams?: Record<string, string | undefined>
): void {
  const normalizedUrl = normalizeRedirectUrl(url);

  if (!normalizedUrl) {
    console.warn('[safeRedirect] No valid URL provided for redirect:', url);
    fallback?.();
    return;
  }

  try {
    let finalUrl = normalizedUrl;

    // Automatically append TSP count to external URLs
    if (normalizedUrl.startsWith('http')) {
      const tspCount = getTspCountFromStorage();
      finalUrl = appendTspCountToUrl(normalizedUrl, tspCount);
      console.log(`[safeRedirect] Added tsp_count=${tspCount} to external URL`);

      // Validate URL format for external URLs
      new URL(finalUrl); // This will throw if invalid
    }

    finalUrl = appendQueryParamsToUrl(finalUrl, extraParams);

    console.log('[safeRedirect] Redirecting to:', finalUrl);
    window.location.href = finalUrl;
  } catch (error) {
    console.error('[safeRedirect] Invalid URL format:', normalizedUrl, error);
    fallback?.();
  }
}

/**
 * Safely redirect using window.location.replace with TSP count
 * Automatically appends TSP count to external URLs
 */
export function safeRedirectReplace(
  url: string | null | undefined,
  fallback?: () => void,
  extraParams?: Record<string, string | undefined>
): void {
  const normalizedUrl = normalizeRedirectUrl(url);

  if (!normalizedUrl) {
    console.warn(
      '[safeRedirectReplace] No valid URL provided for redirect:',
      url
    );
    fallback?.();
    return;
  }

  try {
    let finalUrl = normalizedUrl;

    // Automatically append TSP count to external URLs
    if (normalizedUrl.startsWith('http')) {
      const tspCount = getTspCountFromStorage();
      finalUrl = appendTspCountToUrl(normalizedUrl, tspCount);
      console.log(
        `[safeRedirectReplace] Added tsp_count=${tspCount} to external URL`
      );

      // Validate URL format for external URLs
      new URL(finalUrl); // This will throw if invalid
    }

    finalUrl = appendQueryParamsToUrl(finalUrl, extraParams);

    console.log('[safeRedirectReplace] Redirecting to:', finalUrl);
    window.location.replace(finalUrl);
  } catch (error) {
    console.error(
      '[safeRedirectReplace] Invalid URL format:',
      normalizedUrl,
      error
    );
    fallback?.();
  }
}

/**
 * Check if a URL is external (starts with http/https)
 */
export function isExternalUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const normalized = normalizeRedirectUrl(url);
  return normalized?.startsWith('http') || false;
}

/**
 * Validate if a URL is properly formatted
 */
export function isValidUrl(url: string): boolean {
  try {
    if (url.startsWith('/')) {
      return true; // Valid relative path
    }
    new URL(normalizeRedirectUrl(url) || url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get a user-friendly description of URL issues for error messages
 */
export function getUrlValidationMessage(url: string): string | null {
  if (!url || url.trim() === '') {
    return 'URL cannot be empty';
  }

  const trimmed = url.trim();

  // Check for common mistakes
  if (trimmed.includes(' ')) {
    return 'URL cannot contain spaces';
  }

  if (trimmed.startsWith('www.') && !trimmed.includes('://')) {
    return null; // This will be auto-fixed by normalizeRedirectUrl
  }

  if (!isValidUrl(trimmed)) {
    return 'Please enter a valid URL (e.g., https://example.com or /path)';
  }

  return null;
}
